import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const NetSuiteEnterpriseSolutions = () => {
  const phrases = [
    "OneWorld Global Financials",
    "SuiteScript 2.x Automation",
    "Omnichannel Commerce Sync"
  ];

  const cardData = [
    { type: 'ns-core', content: '🌐' },
    { type: 'gold-wave', content: '📊' },
    { type: 'suitecloud-stack', content: '⚙️' },
    { type: 'script-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🌐", title: "NetSuite OneWorld Multi-Subsidiary Tuning", desc: "Configure global multi-currency ledgers, intercompany automated cross-charges, and real-time localized tax compliance engines safely." },
    { icon: "⚡", title: "SuiteScript 2.x Programmatic Optimization", desc: "Develop low-latency User Event, Scheduled, Map/Reduce, and Suitelet scripts to automate heavy internal operational workflows without hitting governance limits." },
    { icon: "⚙️", title: "Advanced Order & Supply Chain Management", desc: "Streamline multi-location inventory distribution, drop-ship routing paths, demand planning, and automatic fulfillment matrices." },
    { icon: "📊", title: "SuiteAnalytics Workbook Architecture", desc: "Build multi-dimensional data lenses, dynamic financial pivot layouts, and critical operational KPI scorecards for executive visibility." },
    { icon: "🔗", title: "SuiteTalk REST & SOAP Integration Hubs", desc: "Connect NetSuite smoothly with external modern frontends, high-volume e-commerce platforms, and legacy CRM databases via secure web services." },
    { icon: "🛡️", title: "SOX-Compliant Security & Access Governance", desc: "Establish granular role-based permissions (RBAC), customized system segregation of duties (SoD), and fully auditable login matrices." },
    { icon: "🔄", title: "Omnichannel SuiteCommerce Implementations", desc: "Deploy unified core operational ecosystems bridging digital checkout pipelines directly into core warehousing inventory branches." }
  ];

  const advancedServices = [
    { icon: "🔍", title: "NetSuite Analytics Warehouse (NSAW) Sync", desc: "Synthesize high-volume enterprise transactions into localized cloud storage data lakes for heavy predictive trend analysis modeling." },
    { icon: "💼", title: "Advanced Revenue Recognition (ARM) Setup", desc: "Automate complex ASC 606 / IFRS 15 multi-element fair value contract revenue reclassifications with zero spreadsheet dependency." },
    { icon: "🗃️", title: "SuiteBilling Subscription Optimization", desc: "Manage tiered contract usage billing rates, milestone invoicing configurations, and recurring subscription renewal paths easily." },
    { icon: "⚖️", title: "Procure-to-Pay Automation Workflows", desc: "Deploy rigid multi-level custom approval patterns matching purchase requisitions precisely against global corporate budgets." },
    { icon: "🚨", title: "Fixed Assets Management (FAM) Overhauls", desc: "Track full asset lifecycles, configure custom depreciation schedules, and post automated lease accounting entries smoothly." },
    { icon: "📝", title: "Electronic Data Interchange (EDI) Piping", desc: "Architect standard structural document handling templates for major retail distribution exchanges securely." },
    { icon: "🌐", title: "Custom SuiteBuilder Application Extensions", desc: "Design tailored custom records, parent-child table schemas, dynamic subtabs, and complex transaction body fields." },
    { icon: "🧪", title: "Regression Suite (SDF) Environment Controls", desc: "Govern software modifications utilizing the SuiteCloud Development Framework to deploy XML components safely across sandboxes." },
    { icon: "📦", title: "Data Migration Strategy & Legacy Extraction", desc: "Execute programmatic ETL procedures mapping historical accounting records safely into clean target NetSuite closing balances." }
  ];

  const technologies = {
    "Core ERP Modules": ["NetSuite Financials / OneWorld", "Advanced Inventory & WMS", "Fixed Asset Management (FAM)", "SuiteBilling / Core CRM Suite"],
    "SuiteCloud Toolsets": ["SuiteScript 2.0 / 2.1 Engine", "SuiteBuilder Custom Schemas", "SuiteFlow Workflow Designer", "SuiteCloud Development Framework"],
    "Integration Pathways": ["SuiteTalk REST / SOAP Web Services", "NetSuite REST App Endpoints", "Celigo / Boomi Integration Middleware", "Custom Webhook Processors"],
    "Analytics & Reporting": ["SuiteAnalytics Workbooks & Lenses", "NetSuite Analytics Warehouse (NSAW)", "Saved Searches / Custom Financials", "ODBC Database Connectors"],
    "Advanced Accounting": ["Advanced Revenue Management (ARM)", "Multi-Book Accounting Options", "Intercompany Management Matrix", "Electronic Bank Payments Tuning"]
  };

  const processSteps = [
    { num: "01", title: "Business System & Process Auditing", desc: "Analyzing legacy charts of accounts, mapping current fulfillment gaps, and cataloging manual scripting targets." },
    { num: "02", title: "Functional Blueprinting & Sandbox Setup", desc: "Designing custom record schemas, configuring multi-book routing, and sizing cloud sandbox compute targets." },
    { num: "03", title: "SuiteScript & Architecture Production", desc: "Writing clean, event-driven JavaScript handlers, shaping integration endpoints, and structuring approval rules." },
    { num: "04", title: "Data ETL Transformation Sprints", desc: "Scrubbing legacy vendor, customer, and historical transactional ledger strings before running test staging injection runs." },
    { num: "05", title: "User Acceptance & Governance Profiling", desc: "Verifying SuiteScript script credit usage totals, testing custom role boundaries, and reviewing financial reports." },
    { num: "06", title: "Production Cutover & Deployment", desc: "Locking legacy system writes, running final trial balances, and pushing SDF code blocks to the live node safely." },
    { num: "07", title: "Continuous Optimization & Tuning", desc: "Reviewing script execution times via Application Performance Monitoring (APM) tools and deploying minor performance enhancements." }
  ];

  const industries = [
    "High-Growth Global E-Commerce & Distribution Hubs", "Multi-Entity Software-as-a-Service (SaaS) Foundations", "Cross-Border Wholesale Manufacturing & Components",
    "Specialized Professional Services & Resource Optimization", "Advanced Medical Device & Healthcare Supply Networks", "High-Volume Retail Chains & Inventory Allocations",
    "Non-Profit Global Foundations & Fund Management", "Energy, Infrastructure Utilities & Operations Networks", "Commercial Real Estate & Structural Property Portfolios",
    "High-Velocity Electronics Engineering & Logistics", "Media Networks, Digital Agencies & Performance Hubs", "Global Food & Beverage Production & Supply Operations"
  ];

  const designBenefits = [
    "Zero Financial Subsidiary Consolidation Balances Variance", "Up to 50% Reduction in Monthly Accounting Close Duration", "100% Auditable Security Roles & Configuration Logs",
    "Guaranteed Live Production System Environment Operational Uptime", "Drastic Drop in Manual Entry and Spreadsheet Reconciliations", "Highly Optimized Custom Record Schema Configurations",
    "Seamless Cross-Platform Third-Party Integration Pipelines", "Real-Time Operational Performance Dashboard Metrics Ingestion"
  ];

  const whyChooseUs = [
    "Certified NetSuite ERP Consultants & SuiteCloud Developers", "Proven Track Record Managing Complex Multi-Entity Deployments", "Airtight Internal Control and Audit Security Configuration Models",
    "Deep Competency in Complex SuiteScript Performance Optimization", "Platform-Agnostic Middleware Integration Architecture Competency", "Resilient High-Throughput Map/Reduce Processing Formats",
    "End-to-End Environment Transport & Lifespan Governance", "Transparent Engineering Goals & Verifiable Sprint Testing Metrics", "High-Availability Integration Platform Infrastructure SLAs",
    "Experienced Advanced Revenue Management (ARM) Deployment Specialists", "Iterative Sandbox Environment Prototyping Validation Sprints", "100% Production Environment Architecture Stability Assurances"
  ];

  return (
    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          NetSuite Enterprise Solutions | NetSuite Consulting & ERP Implementation | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="NetSuite Enterprise Solutions | NetSuite Consulting & ERP Implementation | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides NetSuite Enterprise Solutions, including NetSuite consulting, ERP implementation, customization, integration, SuiteCloud development, SuiteScript development, cloud ERP migration, and managed NetSuite support services."
        />

        <meta
          name="keywords"
          content="NetSuite Enterprise Solutions, NetSuite Consulting, NetSuite ERP, NetSuite Implementation, NetSuite Customization, NetSuite Integration, Oracle NetSuite, SuiteCloud Development, SuiteScript Development, Cloud ERP Solutions, ERP Migration, Financial Management Software, Business Management Software, NetSuite Managed Services, Enterprise ERP Solutions, NetSuite Support, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/netsuite-enterprise-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional NetSuite Enterprise Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Optimize your business operations with NetSuite Enterprise Solutions from Affsoft IT Solutions Pvt. Ltd. We specialize in NetSuite consulting, ERP implementation, customization, integration, SuiteCloud development, and managed support."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/netsuite-enterprise-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="NetSuite Enterprise Solutions | NetSuite Consulting & ERP Implementation | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Transform your enterprise with NetSuite ERP solutions from Affsoft IT Solutions Pvt. Ltd. We offer NetSuite consulting, implementation, customization, integration, SuiteScript development, and cloud ERP services."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="netsuite-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0c1b2a' // Deep NetSuite Slate Fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Marquee Text */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>NetSuite &nbsp; Cloud &nbsp; ERP &nbsp; &amp; &nbsp;</span>
              <span>SuiteCloud Solutions Architecture </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-amber fs-3" style={{ color: '#e5a93b' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Oracle NetSuite ERP</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the script blockages and tracking variances that limit global business management. Our NetSuite engineering practice pairs auditable OneWorld financial infrastructure setups with clean SuiteScript 2.x development to convert complex transactional workflows into clear strategic spaces.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Scope an ERP Architecture</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#e5a93b' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#e5a93b', color: '#0c1b2a' }}>ERP Systems Scaling</span>
              <h2 className="fw-bold text-dark display-6">Deploy Automated Business Scripts & Secure Ledger Integration Hubs</h2>
              <p className="text-muted mt-3">
                Unoptimized custom script loops, broken inventory integrations, and manual multi-subsidiary adjustments add administrative friction. At Affsoft IT Solutions Pvt. Ltd., we construct airtight, upgrade-safe, and systematically tracked cloud ERP environments.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#e5a93b' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Cloud NetSuite Modernization</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#e5a93b', color: '#0c1b2a' }}>Distributed Architecture Nodes</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Core Capabilities Engineered for Transaction Density</h2>
              <p className="text-muted mt-3">
                We scale basic operational workflows into automated global software environments. From automating complex ASC 606 revenue adjustments to deploying secure SDF release pipelines, our configurations ensure financial record integrity.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#e5a93b' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#e5a93b' }}>NetSuite Stack Blueprint</span>
              <h2 className="fw-bold display-6">ERP Infrastructure Frameworks & Development Toolsets We Standardize</h2>
              <p className="text-light-50">We configure, interface, and maintain enterprise operational networks utilizing strict verification matrices.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#e5a93b' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#e5a93b' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Implementation Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered ERP Deployment Blueprint</h2>
              <p className="text-muted">A structured, strictly governed development lifecycle designed to transition heavy core processes safely from validation passes onto active live production instances.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#e5a93b' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#e5a93b' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine accounting control visibility with clear SuiteScript optimization rules to guarantee highly predictable system behavior.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#e5a93b' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default NetSuiteEnterpriseSolutions;